import {useEffect, useState, useMemo, useRef} from "react";
import {Values} from "@reside/forms";
import {Draft} from "../../admission-explorer/model";
import {FileService} from "../../../services/FileService";
import {logErrorToSentry} from "../../../utils/sentry";

const FILE_ID_PATTERN = /\/api\/files\/([a-f0-9-]+)/i;
const S3_URL_PREFIX = "https://s3.";

// Only process these specific answer keys for pre-signed URLs
// Add more keys here as needed (e.g., "otherFileField")
const KEYS_TO_PROCESS = new Set([
  "facilityHandbook",
  "facilityRights",
  "facilityContactInformationForm",
  "preflight_secondaryCommercialIdCard",
  "facilityCorporateHotline",
  "udalsaDocumentLink",
  "OtherProvidersDocumentLink", // First letter is intentional upercase to match the template key name.
  "residentGuideDocumentLink",
]);

/**
 * Hook that processes specific file answer URLs to get pre-signed S3 URLs.
 *
 * Only processes answer keys listed in KEYS_TO_PROCESS to avoid interfering
 * with other file handling logic (e.g., signatures which have their own flow).
 *
 * @param draft - Draft object containing admission answers
 * @returns Draft with processed file URLs, or undefined if draft is undefined
 */
export const useProcessedFileAnswers = (
  draft: Draft | undefined,
): Draft | undefined => {
  const [processedAnswers, setProcessedAnswers] = useState<Values>({});
  const processedKeysRef = useRef<Set<string>>(new Set());
  const inFlightRequestsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!draft?.answers) {
      setProcessedAnswers({});
      processedKeysRef.current.clear();
      inFlightRequestsRef.current.clear();
      return;
    }

    const processFileAnswers = async () => {
      const answers = draft.answers;
      const updates: Record<string, string> = {};

      for (const [key, value] of Object.entries(answers)) {
        // Only process whitelisted keys
        if (!KEYS_TO_PROCESS.has(key)) continue;

        if (typeof value !== "string") continue;

        const urlMatch = FILE_ID_PATTERN.exec(value);
        if (!urlMatch) continue;

        // Skip if already a pre-signed S3 URL
        if (value.startsWith(S3_URL_PREFIX)) continue;

        const fileId = urlMatch[1];
        const cacheKey = `${key}:${fileId}`;

        // Skip if already processed or currently being fetched
        if (
          processedKeysRef.current.has(cacheKey) ||
          inFlightRequestsRef.current.has(cacheKey)
        ) {
          continue;
        }

        // Mark as in-flight to prevent duplicate requests
        inFlightRequestsRef.current.add(cacheKey);

        try {
          const fileResponse = await FileService.getFileReference(fileId);

          if (fileResponse.ok && fileResponse.data?.url) {
            updates[key] = fileResponse.data.url;
            processedKeysRef.current.add(cacheKey);
          }
        } catch {
          // Silently handle errors - keep original URL
          logErrorToSentry(
            new Error(`Failed to get pre-signed URL for file ${fileId}`),
            {fileId, cacheKey},
          );
        } finally {
          // Remove from in-flight after completion (success or error)
          inFlightRequestsRef.current.delete(cacheKey);
        }
      }

      if (Object.keys(updates).length > 0) {
        setProcessedAnswers(prev => ({...prev, ...updates}));
      }
    };

    processFileAnswers();
  }, [draft?.answers]);

  const draftWithProcessedAnswers = useMemo(() => {
    if (!draft) return draft;

    // Merge processed answers with original answers
    const mergedAnswers = {
      ...draft.answers,
      ...processedAnswers,
    };

    return {
      ...draft,
      answers: mergedAnswers,
    };
  }, [draft, processedAnswers]);

  return draftWithProcessedAnswers;
};
